/**
 * C-GTCC-VIEW-HW-GW2 — typed client for the GTCC view "Hardware & SIMs".
 *
 * Five reads and the two approval writes the reference's `AKTION` cell offers.
 * Four of the five reads are endpoints the old console already had; only the
 * last two paths (`/v1/admin/gtcc/hw/**`) are new, and they are the read-only
 * module `services/api/src/modules/gtcc/hw/` this ticket adds. No write
 * endpoint is new: `[Freigeben]` and `[Ablehnen]` are the established
 * device-approval routes, unchanged.
 *
 *   - `GET /v1/admin/devices/pending` — the Freigabe-Warteschlange and the
 *     tile "WARTEN AUF FREIGABE" (cross-tenant, SECURITY DEFINER; surface
 *     `devices`).
 *   - `GET /v1/admin/gtcc/ops/vehicle-flow` — the tiles "TRACKER GESAMT" and
 *     "STILL > 24 H" including its `16 URBAGEC` sub-line (surface
 *     `data-plane`, J-GTCC-API-OPS1). The field names below are transcribed
 *     from the merged `modules/gtcc/ops/dto.ts`, not from `./ops.ts`, whose
 *     own transcription predates that merge.
 *   - `GET /v1/admin/gtcc/hw/inventory` — the tile "SIM AKTIV" and the two
 *     stock states the reference prints under "Managed Hardware (Lager)"
 *     that no column carries.
 *   - `GET /v1/admin/gtcc/hw/flagged-devices` — the panel "Auffällige Geräte".
 *   - `GET /v1/admin/managed-hardware/pool` — the row "Auf Lager, unverbaut"
 *     (surface `managed-hardware`).
 *
 * Read-only by construction for the reads: `gtccGet` wraps `apiGet` only.
 * Nothing is derived here. Ages arrive as `ageSeconds` decided by the database
 * clock, verdicts and statuses arrive decided by the API, and a value without
 * a source arrives as `null` with a `reason` — the view then prints
 * "nicht messbar" rather than a number nobody measured (Owner P8/P9/P10).
 */
import { apiSend, ApiError } from "../../lib/api.js";
import {
  gtccGet,
  type GtccMeasured,
  type GtccResult,
  type GtccStatus,
  type GtccVerdict,
} from "./gtcc-result.js";
import { OPS_VEHICLE_FLOW_PATH } from "./ops.js";

/* --------------------------------------------------------------- paths */

const HW_BASE = "/v1/admin/gtcc/hw";

export const HW_FLAGGED_DEVICES_PATH = `${HW_BASE}/flagged-devices`;
export const HW_INVENTORY_PATH = `${HW_BASE}/inventory`;
export const HW_PENDING_PATH = "/v1/admin/devices/pending";
export const HW_POOL_PATH = "/v1/admin/managed-hardware/pool";

/** The same page the device-approval view reads; the route caps `limit` at 200. */
export const HW_PENDING_LIMIT = 50;
/** The pool route's own maximum. A full page means the list is truncated. */
export const HW_POOL_LIMIT = 200;

export const HW_PENDING_QUERY_PATH = `${HW_PENDING_PATH}?limit=${HW_PENDING_LIMIT}`;
export const HW_POOL_QUERY_PATH = `${HW_POOL_PATH}?limit=${HW_POOL_LIMIT}`;

/** Read through this module's own name so the view needs one import. */
export const HW_VEHICLE_FLOW_PATH = OPS_VEHICLE_FLOW_PATH;

export function hwApprovePath(deviceId: string): string {
  return `/v1/admin/devices/${encodeURIComponent(deviceId)}/approve`;
}

export function hwRejectPath(deviceId: string): string {
  return `/v1/admin/devices/${encodeURIComponent(deviceId)}/reject`;
}

/** `reason` is bounded 1..500 by the reject route's own schema. */
export const HW_REJECT_REASON_MAX = 500;

/* ---------------------------------------------------------------- types */

/** One measurement of the envelope grammar, `modules/gtcc/hw/dto.ts`. */
export interface HwRowDto {
  readonly key: string;
  readonly label: string;
  readonly verdict: GtccVerdict;
  readonly valueText: string;
  readonly ageSeconds: number | null;
  /** Why there is no measurement — present exactly for `unmeasurable`. */
  readonly reason?: string;
}

/**
 * One conspicuous device. `vehicleLabel` is `null` whenever the vehicle cannot
 * be read cross-tenant, and `vehicleReason` then names the obstacle. A null is
 * therefore "not measurable", never "no vehicle assigned" — the column prints
 * the former, never the latter.
 */
export interface HwFlaggedDeviceDto {
  readonly key: string;
  readonly imei: string;
  readonly tenantName: string | null;
  readonly vehicleLabel: string | null;
  readonly vehicleReason: string | null;
  readonly ageSeconds: number | null;
  readonly occurredAt: string | null;
  readonly problem: string;
  readonly verdict: GtccVerdict;
}

export interface HwFlaggedDevicesDto extends GtccMeasured {
  readonly rows: ReadonlyArray<HwRowDto>;
  readonly windowHours: number;
  readonly truncated: boolean;
  readonly devices: ReadonlyArray<HwFlaggedDeviceDto>;
}

export interface HwInventoryDto extends GtccMeasured {
  readonly rows: ReadonlyArray<HwRowDto>;
  readonly simTotal: number | null;
  readonly simActive: number | null;
  /** No column carries consumed volume; this is `null` with a row reason. */
  readonly simNearDataLimit: number | null;
}

/** Transcribed from `gtccVehicleFlowSchema`, `modules/gtcc/ops/dto.ts:31`. */
export interface HwVehicleFlowDto extends GtccMeasured {
  readonly rows: ReadonlyArray<HwRowDto>;
  readonly sending: number | null;
  readonly total: number | null;
  readonly silent24h: number | null;
  readonly positionsPerMin: number | null;
  readonly largestTenant: {
    readonly name: string | null;
    readonly sending: number | null;
    readonly total: number | null;
  };
}

/** Mirrors `PendingDeviceListRow`, `admin/device-approval.service.ts:54`. */
export interface HwPendingDeviceDto {
  readonly id: string;
  readonly tenantId: string | null;
  readonly tenantDisplayName: string | null;
  readonly tenantSlug: string | null;
  readonly imei: string;
  readonly deviceType: string;
  /** First contact, an absolute timestamp — never an age from this clock. */
  readonly createdAt: string;
}

export interface HwPendingDevicesDto {
  readonly items: ReadonlyArray<HwPendingDeviceDto>;
  readonly truncated: boolean;
}

export interface HwPoolDeviceDto {
  readonly id: string;
  readonly imei: string;
  readonly deviceType: string;
  readonly status: string;
  readonly createdAt: string;
}

export interface HwPoolDto {
  readonly items: ReadonlyArray<HwPoolDeviceDto>;
  readonly truncated: boolean;
}

export interface HwApproveRequest {
  readonly tenantId: string;
}

export interface HwRejectRequest {
  readonly tenantId: string;
  readonly reason: string;
}

/* -------------------------------------------------------------- fetchers */

export function fetchHwFlaggedDevices(
  signal?: AbortSignal,
): Promise<GtccResult<HwFlaggedDevicesDto>> {
  return gtccGet<HwFlaggedDevicesDto>(HW_FLAGGED_DEVICES_PATH, signal);
}

export function fetchHwInventory(signal?: AbortSignal): Promise<GtccResult<HwInventoryDto>> {
  return gtccGet<HwInventoryDto>(HW_INVENTORY_PATH, signal);
}

export function fetchHwVehicleFlow(signal?: AbortSignal): Promise<GtccResult<HwVehicleFlowDto>> {
  return gtccGet<HwVehicleFlowDto>(HW_VEHICLE_FLOW_PATH, signal);
}

export function fetchHwPendingDevices(
  signal?: AbortSignal,
): Promise<GtccResult<HwPendingDevicesDto>> {
  return gtccGet<HwPendingDevicesDto>(HW_PENDING_QUERY_PATH, signal);
}

export function fetchHwPool(signal?: AbortSignal): Promise<GtccResult<HwPoolDto>> {
  return gtccGet<HwPoolDto>(HW_POOL_QUERY_PATH, signal);
}

/* ---------------------------------------------------------------- writes */

/**
 * The two approval writes fold into the same result union as the reads, so the
 * view reports a refused decision instead of letting an `ApiError` escape. The
 * routes are untouched; this ticket adds no write endpoint.
 */
function toHwFailure<T>(err: unknown): GtccResult<T> {
  if (err instanceof ApiError) {
    const { status, requestId } = err;
    if (status === 404) return { ok: false, kind: "disabled", httpStatus: 404 };
    if (status === 403) return { ok: false, kind: "forbidden", httpStatus: 403 };
    return { ok: false, kind: "upstream", httpStatus: status, requestId };
  }
  const message = err instanceof Error ? err.message : "gtcc hw request failed";
  return { ok: false, kind: "transport_error", message };
}

/** The decided device, as `POST .../approve` and `.../reject` both answer it. */
export interface HwDecisionDto {
  readonly device?: { readonly id?: string; readonly status?: string };
}

export async function approveHwDevice(
  deviceId: string,
  request: HwApproveRequest,
  signal?: AbortSignal,
): Promise<GtccResult<HwDecisionDto>> {
  try {
    return {
      ok: true,
      data: await apiSend<HwDecisionDto>("POST", hwApprovePath(deviceId), request, signal),
    };
  } catch (err) {
    return toHwFailure<HwDecisionDto>(err);
  }
}

export async function rejectHwDevice(
  deviceId: string,
  request: HwRejectRequest,
  signal?: AbortSignal,
): Promise<GtccResult<HwDecisionDto>> {
  try {
    return {
      ok: true,
      data: await apiSend<HwDecisionDto>("POST", hwRejectPath(deviceId), request, signal),
    };
  } catch (err) {
    return toHwFailure<HwDecisionDto>(err);
  }
}

/* ----------------------------------------------------------- query keys */

/*
 * Prefix convention of the GTCC query-key inventory: `["gtcc", <viewkey>, …]`.
 * The view boundary cancels and clears by exactly this prefix. The pool key
 * says `managed-pool` rather than `pool` because the legacy module
 * `views/hw/GtccHardwareView.tsx` still owns `["gtcc","hw","pool"]` on disk.
 */
export const HW_FLAGGED_DEVICES_KEY = ["gtcc", "hw", "flagged-devices"] as const;
export const HW_INVENTORY_KEY = ["gtcc", "hw", "inventory"] as const;
export const HW_VEHICLE_FLOW_KEY = ["gtcc", "hw", "ops-vehicle-flow"] as const;
export const HW_PENDING_KEY = ["gtcc", "hw", "pending-devices"] as const;
export const HW_POOL_KEY = ["gtcc", "hw", "managed-pool"] as const;

export const HW_QUERY_KEYS = {
  flaggedDevices: HW_FLAGGED_DEVICES_KEY,
  inventory: HW_INVENTORY_KEY,
  vehicleFlow: HW_VEHICLE_FLOW_KEY,
  pendingDevices: HW_PENDING_KEY,
  pool: HW_POOL_KEY,
} as const;

/** Re-exported so consumers of this module need one import, not two. */
export type { GtccResult, GtccStatus, GtccVerdict };
